import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ForgotLoginScreenContent,
  LoginFormValidate
} from '@com-delta-idp/mnx-login-ui';
import { ChallengeScreenError, OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import { AnalyticsService, LoggingService } from '@com-delta-idp/mnx-utils';
import { BehaviorSubject } from 'rxjs';
import { LoginHelperUtils } from '../../../utils/login-helper.utils';
import { ForgotLoginAdapterService } from '../../adapters/forgot-login-adapter/forgot-login-adapter.service';
import { ANALYTICS_EVENTS_LOGIN } from '../../constants/analytics-constants';
import { ERROR_CODES, VIEW_NAME } from '../../constants/login-constant';
import { LoginModalContent } from '../../models/aem';
import { forgotLoginRequestPayload } from '../../models/forgot-login-interface';
import { error } from '../../models/reset-password-api.interface';
import {
  ForgotLoginContentService,
  LoginContentService,
  NextDisplayViewService,
  PingOauthService
} from '../../services';
import { CheckEmailFacadeService } from '../check-email-facade/check-email-facade.service';
import { FacadeService } from '../facade.service';

@Injectable({
  providedIn: 'root'
})
export class ForgotLoginFacadeHelperService {
  aemContent!: LoginModalContent;
  authenticationConfig!: OauthAEMConfig;
  forgotLoginContent$: BehaviorSubject<ForgotLoginScreenContent | undefined> =
    new BehaviorSubject<ForgotLoginScreenContent | undefined>(undefined);

  loginHelperUtils = inject(LoginHelperUtils);
  checkEmailFacadeService = inject(CheckEmailFacadeService);
  loggingService = inject(LoggingService);
  forgotLoginAdapter = inject(ForgotLoginAdapterService);
  forgotLoginContentService = inject(ForgotLoginContentService);
  facadeService = inject(FacadeService);
  loginContentService = inject(LoginContentService);
  nextDisplayViewService = inject(NextDisplayViewService);
  pingOauthService = inject(PingOauthService);
  analytics = inject(AnalyticsService);

  disableButton(updateValue: boolean): void {
    this.getForgotLoginContent().disableButton = updateValue;
  }

  getForgotLoginContent(): ForgotLoginScreenContent {
    return this.forgotLoginContent$.value || ({} as ForgotLoginScreenContent);
  }

  getRequestPayload(): forgotLoginRequestPayload {
    const formGroup = this.getForgotLoginContent().formGroup;
    const request = {
      abcyz: '',
      email: formGroup.get('emailId')?.value?.value?.trim(),
      firstName: formGroup.get('firstName')?.value?.value?.trim(),
      formNameSubmitted: 'forgotLogin',
      lastName: formGroup.get('lastName')?.value?.value?.trim()
    };
    return request;
  }

  resetForm = () => {
    if (this.getForgotLoginContent().forgotLoginAttributes) {
      this.getForgotLoginContent().forgotLoginAttributes.submitAttempt = false;
      this.getForgotLoginContent().forgotLoginAttributes.emailIdControlTouched =
        false;
      this.getForgotLoginContent().forgotLoginAttributes.firstNameControlTouched =
        false;
      this.getForgotLoginContent().forgotLoginAttributes.lastNameControlTouched =
        false;
      this.getForgotLoginContent().errorCode = undefined;
      this.getForgotLoginContent().formGroup?.reset();
      this.disableButton(false);
    }
  };

  handleError(errors: error[]): void {
    const errorCode =
      errors instanceof Array && errors[0]?.code
        ? errors[0]?.code
        : ERROR_CODES.ERROR_CODE_21;
    this.getForgotLoginContent().forgotLoginContent.errors.formAlertErrorMessage =
      this.loginHelperUtils.getErrorMessageFromAem(
        this.aemContent,
        `ERROR_MSG_${errorCode}`
      );
    this.getForgotLoginContent().errorCode = errorCode;
    this.disableButton(false);
  }

  setForgotLoginFormValues(request: forgotLoginRequestPayload) {
    this.checkEmailFacadeService.newForgotLoginFlow = true;
    this.checkEmailFacadeService.getCheckEmailPageContent();
    const values = {
      skymilesNumber: request.email,
      firstName: request.firstName,
      lastName: request.lastName
    };
    this.checkEmailFacadeService.resetLoginPasswordFormValues$.next(values);
  }

  redirectToCheckYourEmail() {
    this.facadeService.getContent().loginFormAttributes.formAlertSuccessIcon =
      true;
    this.facadeService.getContent().errorCode = undefined;
    this.facadeService.getContent().loginFormAttributes.formAlertSuccessIcon =
      false;
    this.analytics.dispatchCustomEvent(
      ANALYTICS_EVENTS_LOGIN.CHECK_YOUR_EMAIL_FL_LOADED,
      true
    );
    this.nextDisplayViewService.nextDisplayView(
      VIEW_NAME.CHECK_YOUR_EMAIL_SCREEN
    );
  }

  redirectEmailScreen(
    err: HttpErrorResponse | ChallengeScreenError,
    request: forgotLoginRequestPayload,
    authConfig: OauthAEMConfig
  ) {
    if (authConfig.NEW_RESET_PASSWORD_FLOW) {
      if (!err?.error?.errors) {
        this.setForgotLoginFormValues(request);
        this.redirectToCheckYourEmail();
        return;
      }
    }
    this.handleError(err?.error?.errors);
  }

  closeOverLayMessage() {
    this.getForgotLoginContent().errorCode = undefined;
  }

  focusChangeForgotLogin(fieldName: string) {
    LoginFormValidate.touchForgotLoginControl(
      fieldName,
      this.getForgotLoginContent()
    );
  }
}
